import { useCallback, useState } from 'react';
import { removeBlockWithFeedback } from '../helpers/removeBlockWithFeedback';
import { TextBlock } from '../interfaces/textEditor.interface';

interface UseBlockRemovalArgs {
  username: string;
  draftId: string;
  /** Prune the caller's own saved-key list once an image is confirmed deleted. */
  onKeyRemoved: (key: string) => void;
}

/**
 * Owns block removal AND the error banner state for both composers.
 *
 * WHY A HOOK RATHER THAN A TESTED CALLBACK (TRIL-615 review). The previous shape passed
 * `onError: setImageDeleteError` from each component, and review found — twice — that replacing it
 * with a no-op left the whole suite green. Each round the finding just moved one level of
 * indirection down, because the thing being asserted was that a component remembered to wire two
 * pieces together.
 *
 * So the onError-passing seam is gone: the state and the call live in one place.
 *
 * WHAT THIS DOES NOT COVER, stated because the previous version of this comment overclaimed that "a
 * component cannot hold this wrong". It can, in TWO places, and an earlier version of this note
 * named only the first:
 *
 *   1. each composer has to RENDER `imageDeleteError` — delete that JSX and the suite stays green,
 *      and a silent banner is precisely the failure this ticket exists to prevent;
 *   2. each composer has to WIRE `onClick` to `removeBlock` — a delete button that calls nothing
 *      leaves the suite green too, and is a worse failure than a missing banner, since the block
 *      is never removed at all.
 *
 * Both need a rendered test per composer (Formik + Redux + Router harness) and are TRIL-626. The
 * seams are single visible lines rather than multi-part wiring, which is smaller, not closed.
 */
export const useBlockRemoval = ({
  username,
  draftId,
  onKeyRemoved,
}: UseBlockRemovalArgs) => {
  const [imageDeleteError, setImageDeleteError] = useState<string | null>(null);
  const [isRemoving, setIsRemoving] = useState(false);

  const removeBlock = useCallback(
    // biome-ignore lint/suspicious/noExplicitAny: Formik arrayHelpers
    async (block: TextBlock, arrayHelpers: any, index: number) => {
      setIsRemoving(true);
      setImageDeleteError(null);
      try {
        await removeBlockWithFeedback({
          block,
          username,
          draftId,
          arrayHelpers,
          index,
          onKeyRemoved,
          onError: setImageDeleteError,
        });
      } finally {
        setIsRemoving(false);
      }
    },
    [username, draftId, onKeyRemoved],
  );

  return { removeBlock, imageDeleteError, isRemoving };
};
